import React, { useState, useEffect, useRef } from 'react';
import { X, Building2, User, Users, FileText, Save, Eye, Upload, Trash2, Image, Send, Clock, BarChart2 } from 'lucide-react';
import { uploadToS3 } from '../utils/api';
import { canAssignSubmissions } from '../utils/rbac';
import { CommentsSection } from './CommentsSection';
import { PropertyCompsSection } from './PropertyCompsSection';
import { AssigneeSelector } from './AssigneeSelector';
import { fetchAuthSession } from 'aws-amplify/auth';
import { formatPhoneNumber } from '../utils/formatting';
import { DualScorePillar } from './deals/DealScoreParts';
import type { DealScore } from '../data/dealHealth';

interface JVSubmissionDetailsModalProps {
  submission: any;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (updatedSubmission: any) => Promise<void>;
  mode: 'view' | 'edit';
  isAdmin?: boolean;
  userGroups?: string[]; // Cognito groups for RBAC checks
  currentUserEmail?: string; // For ownership checking
  scrollToComments?: boolean; // Auto-scroll to comments section
  scrollToPropertyAnalysis?: boolean; // Auto-scroll to Property Analysis / comps section
  dealScore?: DealScore | null;
}

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800' },
  { value: 'follow-up', label: 'Follow-Up', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'hot-lead', label: 'Hot Lead', color: 'bg-red-100 text-red-800' },
  { value: 'option-sent', label: 'Option Sent', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'contract-pending', label: 'Contract Pending', color: 'bg-orange-100 text-orange-800' },
  { value: 'under-contract', label: 'Under Contract', color: 'bg-green-100 text-green-800' },
  { value: 'appointment-scheduled', label: 'Appointment Scheduled', color: 'bg-purple-100 text-purple-800' },
  { value: 'closing-schedule', label: 'Closing Schedule', color: 'bg-teal-100 text-teal-800' },
  { value: 'student-action-required', label: 'Student Action Required', color: 'bg-amber-100 text-amber-800' },
  { value: 'closed', label: 'Closed', color: 'bg-green-800 text-white' },
  { value: 'cold', label: 'Cold', color: 'bg-gray-200 text-gray-600' },
  { value: 'dead', label: 'Dead', color: 'bg-gray-900 text-white' },
  { value: 'do-not-contact', label: 'Do Not Contact', color: 'bg-rose-100 text-rose-800' },
  { value: 'bad-phone-number', label: 'Bad Phone Number', color: 'bg-amber-100 text-amber-800' },
  { value: 'warm-lead', label: 'Warm Lead', color: 'bg-lime-100 text-lime-800' },
  { value: 'unresponsive', label: 'Unresponsive', color: 'bg-slate-100 text-slate-800' },
];

export function JVSubmissionDetailsModal({ submission, isOpen, onClose, onSave, mode, isAdmin = false, userGroups = [], currentUserEmail = '', scrollToComments = false, scrollToPropertyAnalysis = false, dealScore = null }: JVSubmissionDetailsModalProps) {
  const canAssign = canAssignSubmissions(userGroups);
  const [editMode, setEditMode] = useState(mode === 'edit');
  const [showCompsPopup, setShowCompsPopup] = useState(false);
  const commentsRef = useRef<HTMLDivElement>(null);
  const propertyAnalysisRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Request & Escalation contact form state
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [showEscalationForm, setShowEscalationForm] = useState(false);
  const [requestType, setRequestType] = useState('');
  const [requestDetails, setRequestDetails] = useState('');
  const [escalationSubject, setEscalationSubject] = useState('');
  const [escalationDetails, setEscalationDetails] = useState('');
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [requestMsg, setRequestMsg] = useState('');
  const [escalationMsg, setEscalationMsg] = useState('');

  // NOTE: isOwner is computed AFTER `sub` is defined below — the `submission` prop is the
  // list item, whose projection omits createdBy, so ownership must be read from the
  // re-fetched full submission (`sub`). See the const isOwner right after `sub`.
  const [status, setStatus] = useState(submission?.status || 'new');
  const [assignedTo, setAssignedTo] = useState(submission?.assignedTo || '');
  const [saving, setSaving] = useState(false);
  const [commentTab, setCommentTab] = useState<'user' | 'admin'>('user');

  // Inline status-bar editing (status + assigned-to) — replaces the old Management panel.
  // No auto-save: pending changes only persist when Save is clicked.
  const [isEditingStatusBar, setIsEditingStatusBar] = useState(false);
  const [inlineStatus, setInlineStatus] = useState(submission?.status || 'new');
  const [inlineAssignedTo, setInlineAssignedTo] = useState(submission?.assignedTo || '');
  const [savingStatusBar, setSavingStatusBar] = useState(false);
  const [statusBarError, setStatusBarError] = useState('');

  // Full submission data fetched from admin detail endpoint
  const [fullSubmission, setFullSubmission] = useState<any>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // State-change history (admin-only audit timeline from the tdb-jv-history ledger)
  const [history, setHistory] = useState<any[] | null>(null);

  // Closing Details (admin-filled listing link + sale amount; the partner sees the card only
  // once something is filled in — Michael, 2026-09-18)
  const [closingLink, setClosingLink] = useState('');
  const [closingAmount, setClosingAmount] = useState('');
  const [savingClosing, setSavingClosing] = useState(false);
  const [closingError, setClosingError] = useState<string | null>(null);
  const [closingSaved, setClosingSaved] = useState(false);
  // Reset the save/error indicators only when a different submission is shown. Keying this on
  // the saved values as well made the very render that shows "Saved" re-run it and clear the
  // flag again (CUJ 040-01, build f63e87ef: PUT 200 but "Saved" never visible).
  useEffect(() => {
    setClosingError(null);
    setClosingSaved(false);
  }, [submission?.id]);
  // Sync the inputs from whatever record we have (list item first, full detail once fetched).
  useEffect(() => {
    const d: any = fullSubmission || submission;
    setClosingLink(d?.propertyListingLink || '');
    setClosingAmount(d?.propertySaleAmount || '');
  }, [submission?.id, fullSubmission?.propertyListingLink, fullSubmission?.propertySaleAmount]);

  // Auto-scroll to comments when modal opens from notification
  // Re-scrolls after fullSubmission fetch completes (loadingDetail goes false)
  // Uses instant scroll for reliability; delay ensures DOM has settled after form resets
  useEffect(() => {
    if (isOpen && scrollToComments && !loadingDetail && scrollContainerRef.current && commentsRef.current) {
      const timer = setTimeout(() => {
        const container = scrollContainerRef.current;
        const target = commentsRef.current;
        if (container && target) {
          const containerRect = container.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();
          container.scrollTop += targetRect.top - containerRect.top;
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen, scrollToComments, loadingDetail]);

  useEffect(() => {
    if (isOpen && scrollToPropertyAnalysis && !loadingDetail && scrollContainerRef.current && propertyAnalysisRef.current) {
      const timer = setTimeout(() => {
        const container = scrollContainerRef.current;
        const target = propertyAnalysisRef.current;
        if (container && target) {
          const containerRect = container.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();
          container.scrollTop += targetRect.top - containerRect.top;
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen, scrollToPropertyAnalysis, loadingDetail]);

  // Fetch full submission details when modal opens
  useEffect(() => {
    if (!isOpen || !submission?.id) {
      setFullSubmission(null);
      return;
    }

    let cancelled = false;
    const fetchFullSubmission = async () => {
      setLoadingDetail(true);
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (!idToken) return;

        const apiUrl = import.meta.env.VITE_API_URL;
        const endpoint = isAdmin
          ? `${apiUrl}/admin/jv-submissions/${submission.id}`
          : `${apiUrl}/jv-submissions/${submission.id}`;
        const response = await fetch(endpoint, {
          headers: { 'Authorization': `Bearer ${idToken}` },
        });

        if (response.ok && !cancelled) {
          const data = await response.json();
          setFullSubmission(data);
        }
      } catch (err) {
        console.error('Failed to fetch full submission details:', err);
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    };

    fetchFullSubmission();
    return () => { cancelled = true; };
  }, [isOpen, submission?.id, isAdmin]);

  // Fetch the deal's state-change history (admin-only endpoint) when the modal opens for an admin.
  // Best-effort: on any failure we just render nothing (no impact on the rest of the modal).
  useEffect(() => {
    if (!isOpen || !isAdmin || !submission?.id) { setHistory(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (!idToken) return;
        const apiUrl = import.meta.env.VITE_API_URL;
        const res = await fetch(`${apiUrl}/admin/jv-submissions/${submission.id}/history`, {
          headers: { 'Authorization': `Bearer ${idToken}` },
        });
        if (res.ok && !cancelled) {
          const data = await res.json();
          setHistory(Array.isArray(data.events) ? data.events : []);
        }
      } catch (err) {
        console.error('Failed to fetch submission history:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, isAdmin, submission?.id]);

  // Use full submission data when available, fall back to list data
  const sub = fullSubmission || submission;

  // Ownership check — MUST read from `sub` (the re-fetched full submission), NOT the
  // `submission` prop: the list projection omits createdBy, so the prop's createdBy is
  // undefined and the Edit/Save button would never render for the true owner.
  // Case-insensitive so mixed-case legacy emails still match.
  const isOwner = (sub?.createdBy || '').toLowerCase() === (currentUserEmail || '').toLowerCase();

  // Editable submission fields state
  const [formData, setFormData] = useState({
    // Partner Info
    partnerFirstName: submission?.partnerFirstName || '',
    partnerLastName: submission?.partnerLastName || '',
    partnerEmail: submission?.partnerEmail || '',
    partnerPhone: submission?.partnerPhone || '',
    partnerTaxCompany: submission?.partnerTaxCompany || '',
    taxAssistanceWebsite: submission?.taxAssistanceWebsite || '',

    jvSplit: submission?.jvSplit || '',
    // Owner Info
    ownerFirstName: submission?.ownerFirstName || '',
    ownerLastName: submission?.ownerLastName || '',
    ownerPhone: submission?.ownerPhone || '',
    ownerEmail: submission?.ownerEmail || '',
    mailingStreet: submission?.mailingStreet || '',
    mailingCity: submission?.mailingCity || '',
    mailingState: submission?.mailingState || '',
    mailingZip: submission?.mailingZip || '',
    bestContactMethod: submission?.bestContactMethod || '',
    bestContactTime: submission?.bestContactTime || '',
    dealType: submission?.dealType || '',
    // Property Details
    propertyStreet: submission?.propertyStreet || '',
    propertyCity: submission?.propertyCity || '',
    propertyState: submission?.propertyState || '',
    propertyZip: submission?.propertyZip || '',
    propertyCounty: submission?.propertyCounty || '',
    taxSaleDate: submission?.taxSaleDate || '',
    taxSaleAmount: submission?.taxSaleAmount || '',
    bedrooms: submission?.bedrooms || '',
    bathrooms: submission?.bathrooms || '',
    sqft: submission?.sqft || '',
    ownershipLength: submission?.ownershipLength || '',
    hasInsurance: submission?.hasInsurance || '',
    repairCost: submission?.repairCost || '',
    propertyCondition: submission?.propertyCondition || '',
    countyTaxRules: submission?.countyTaxRules || '',
    publicTaxRecordLink: submission?.publicTaxRecordLink || '',
    hasMortgage: submission?.hasMortgage || '',
    mortgageBank: submission?.mortgageBank || '',
    mortgageBalance: submission?.mortgageBalance || '',
    mortgagePayment: submission?.mortgagePayment || '',
    // Financial
    arv: submission?.arv || '',
    suggestedOffer: submission?.suggestedOffer || '',
    // Seller Situation
    employmentStatus: submission?.employmentStatus || '',
    monthlyIncome: submission?.monthlyIncome || '',
    incomeBreakdown: submission?.incomeBreakdown || '',
    sellerRealistic: submission?.sellerRealistic || '',
    sellerWantsToStay: submission?.sellerWantsToStay || '',
    optionsDiscussed: submission?.optionsDiscussed || '',
  });

  // File management state
  const [newMaoReports, setNewMaoReports] = useState<File[]>([]);
  const [newPropertyPhotos, setNewPropertyPhotos] = useState<File[]>([]);
  const [newContactLibrary, setNewContactLibrary] = useState<File[]>([]);
  const [filesToRemove, setFilesToRemove] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  // Reset editMode when mode prop changes or modal opens/closes
  React.useEffect(() => {
    setEditMode(mode === 'edit');
  }, [mode, isOpen, submission?.id]);

  // Reset form state when submission or full data changes
  React.useEffect(() => {
    const s = fullSubmission || submission;
    if (s) {
      setStatus(s.status || 'new');
      setAssignedTo(s.assignedTo || '');
      setFormData({
        partnerFirstName: s.partnerFirstName || '',
        partnerLastName: s.partnerLastName || '',
        partnerEmail: s.partnerEmail || '',
        partnerPhone: s.partnerPhone || '',
        partnerTaxCompany: s.partnerTaxCompany || '',
        taxAssistanceWebsite: s.taxAssistanceWebsite || '',
        jvSplit: s.jvSplit || '',
        ownerFirstName: s.ownerFirstName || '',
        ownerLastName: s.ownerLastName || '',
        ownerPhone: s.ownerPhone || '',
        ownerEmail: s.ownerEmail || '',
        mailingStreet: s.mailingStreet || '',
        mailingCity: s.mailingCity || '',
        mailingState: s.mailingState || '',
        mailingZip: s.mailingZip || '',
        bestContactMethod: s.bestContactMethod || '',
        bestContactTime: s.bestContactTime || '',
        dealType: s.dealType || '',
        propertyStreet: s.propertyStreet || '',
        propertyCity: s.propertyCity || '',
        propertyState: s.propertyState || '',
        propertyZip: s.propertyZip || '',
        propertyCounty: s.propertyCounty || '',
        taxSaleDate: s.taxSaleDate || '',
        taxSaleAmount: s.taxSaleAmount || '',
        bedrooms: s.bedrooms || '',
        bathrooms: s.bathrooms || '',
        sqft: s.sqft || '',
        ownershipLength: s.ownershipLength || '',
        hasInsurance: s.hasInsurance || '',
        repairCost: s.repairCost || '',
        propertyCondition: s.propertyCondition || '',
        countyTaxRules: s.countyTaxRules || '',
        publicTaxRecordLink: s.publicTaxRecordLink || '',
        hasMortgage: s.hasMortgage || '',
        mortgageBank: s.mortgageBank || '',
        mortgageBalance: s.mortgageBalance || '',
        mortgagePayment: s.mortgagePayment || '',
        arv: s.arv || '',
        suggestedOffer: s.suggestedOffer || '',
        employmentStatus: s.employmentStatus || '',
        monthlyIncome: s.monthlyIncome || '',
        incomeBreakdown: s.incomeBreakdown || '',
        sellerRealistic: s.sellerRealistic || '',
        optionsDiscussed: s.optionsDiscussed || '',
      });
      setNewMaoReports([]);
      setNewPropertyPhotos([]);
      setNewContactLibrary([]);
      setFilesToRemove([]);
    }
  }, [submission?.id, fullSubmission]);

  if (!isOpen || !submission) return null;

  // For display: use full data if fetched, otherwise fall back to list data
  // (formData is already synced via the useEffect above)
  const displayData = fullSubmission || submission;

  const handleSave = async () => {
    if (!onSave) return;

    setSaving(true);
    setUploading(true);

    try {
      // Upload new files to S3
      const newMaoUrls: string[] = [];
      const newPhotoUrls: string[] = [];
      const newDocUrls: string[] = [];

      if (newMaoReports.length > 0) {
        for (const file of newMaoReports) {
          const url = await uploadToS3(file, 'mao-reports');
          newMaoUrls.push(url);
        }
      }

      if (newPropertyPhotos.length > 0) {
        for (const file of newPropertyPhotos) {
          const url = await uploadToS3(file, 'property-photos');
          newPhotoUrls.push(url);
        }
      }

      if (newContactLibrary.length > 0) {
        for (const file of newContactLibrary) {
          const url = await uploadToS3(file, 'documents');
          newDocUrls.push(url);
        }
      }

      // Combine existing URLs (minus removed) with new URLs
      const updatedMaoUrls = [
        ...(displayData.maoReportUrls || []).filter((url: string) => !filesToRemove.includes(url)),
        ...newMaoUrls
      ];

      const updatedPhotoUrls = [
        ...(displayData.propertyPhotoUrls || []).filter((url: string) => !filesToRemove.includes(url)),
        ...newPhotoUrls
      ];

      const updatedDocUrls = [
        ...(displayData.additionalDocUrls || []).filter((url: string) => !filesToRemove.includes(url)),
        ...newDocUrls
      ];

      await onSave({
        ...displayData,
        ...formData,
        maoReportUrls: updatedMaoUrls,
        propertyPhotoUrls: updatedPhotoUrls,
        additionalDocUrls: updatedDocUrls
      });
      onClose();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  // Save the admin-only Closing Details (PUT /admin/jv-submissions/{id} via onSave)
  const handleSaveClosingDetails = async () => {
    if (!onSave) return;
    const link = closingLink.trim();
    const amount = closingAmount.trim();
    if (link && !/^https?:\/\/\S+$/i.test(link)) {
      setClosingError('Listing link must start with http:// or https://');
      return;
    }
    setSavingClosing(true);
    setClosingError(null);
    setClosingSaved(false);
    try {
      await onSave({ ...displayData, propertyListingLink: link, propertySaleAmount: amount });
      setFullSubmission((prev: any) => ({ ...(prev || displayData), propertyListingLink: link, propertySaleAmount: amount }));
      setClosingLink(link);
      setClosingAmount(amount);
      setClosingSaved(true);
    } catch (err) {
      setClosingError(err instanceof Error ? err.message : 'Failed to save closing details');
    } finally {
      setSavingClosing(false);
    }
  };

  // Enter inline edit mode for the top status bar (admins only)
  const enterStatusBarEdit = () => {
    setInlineStatus(status);
    setInlineAssignedTo(assignedTo);
    setStatusBarError('');
    setIsEditingStatusBar(true);
  };

  // Save only appears once something actually changed (no auto-save)
  const hasStatusBarChanges = inlineStatus !== status || inlineAssignedTo !== assignedTo;

  const handleStatusBarSave = async () => {
    if (!onSave) return;
    setSavingStatusBar(true);
    setStatusBarError('');
    try {
      await onSave({ ...displayData, status: inlineStatus, assignedTo: inlineAssignedTo });
      setStatus(inlineStatus);
      setAssignedTo(inlineAssignedTo);
      setIsEditingStatusBar(false);
    } catch {
      setStatusBarError("Couldn't save, please try again.");
    } finally {
      setSavingStatusBar(false);
    }
  };

  const getStatusColor = (statusValue: string) => {
    return STATUS_OPTIONS.find(s => s.value === statusValue)?.color || 'bg-gray-100 text-gray-800';
  };

  const updateFormField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitRequest = async () => {
    if (!requestType) return;
    setContactSubmitting(true);
    setRequestMsg('');
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      const payload: { requestType: string; details?: string } = { requestType };
      if (requestType === 'Something Else' && requestDetails.trim()) {
        payload.details = requestDetails.trim();
      }
      const res = await fetch(`${import.meta.env.VITE_API_URL}/jv-submissions/${displayData?.id}/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Request failed');
      setRequestMsg('Your request has been sent ✅');
      setTimeout(() => {
        setShowRequestForm(false);
        setRequestType('');
        setRequestDetails('');
        setRequestMsg('');
      }, 2000);
    } catch {
      setRequestMsg('Something went wrong, please try again.');
    } finally {
      setContactSubmitting(false);
    }
  };

  const handleSubmitEscalation = async () => {
    if (!escalationSubject.trim() || !escalationDetails.trim()) return;
    setContactSubmitting(true);
    setEscalationMsg('');
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      const res = await fetch(`${import.meta.env.VITE_API_URL}/jv-submissions/${displayData?.id}/escalation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ subject: escalationSubject.trim(), details: escalationDetails.trim() }),
      });
      if (!res.ok) throw new Error('Escalation failed');
      setEscalationMsg('Your escalation has been sent ✅');
      setTimeout(() => {
        setShowEscalationForm(false);
        setEscalationSubject('');
        setEscalationDetails('');
        setEscalationMsg('');
      }, 2000);
    } catch {
      setEscalationMsg('Something went wrong, please try again.');
    } finally {
      setContactSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-tdb-primary to-tdb-secondary text-white px-8 py-6 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-3 min-w-0">
            <Building2 className="w-6 h-6 shrink-0" />
            <div className="min-w-0">
              <h2 className="text-2xl font-bold">JV Submission Details</h2>
              <p className="text-sm text-gray-200 truncate">
                {displayData.partnerFirstName} {displayData.partnerLastName} - {displayData.propertyStreet}
              </p>
            </div>
            {isAdmin && dealScore && (
              <div className="ml-4 shrink-0">
                <DualScorePillar
                  wholesale={dealScore.scores.wholesale}
                  rentback={dealScore.scores.rentback}
                  recommended={dealScore.recommendedStrategy}
                  size="sm"
                />
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {!editMode && onSave && (isAdmin || isOwner) && (
              <button
                data-testid="edit-submission-button"
                onClick={() => setEditMode(true)}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition-all"
              >
                Edit
              </button>
            )}
            {isAdmin && !editMode && (
              <button
                title="Property Analysis"
                onClick={() => setShowCompsPopup(true)}
                className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
              >
                <BarChart2 className="w-6 h-6" />
              </button>
            )}
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-8">
          {/* Status + Assigned-To editing live in the inline top status bar below (admins).
              Admin Notes removed — the Admin discussion thread at the bottom replaces it. */}

          {/* File Management Section - Available to admins and submission owners in edit mode */}
          {editMode && (isAdmin || isOwner) && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
              <h3 className="font-bold text-tdb-primary mb-4">File Management</h3>

              {/* Upload New Files */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Upload MAO Reports
                  </label>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      if (e.target.files) {
                        setNewMaoReports([...newMaoReports, ...Array.from(e.target.files)]);
                      }
                    }}
                    className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {newMaoReports.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {newMaoReports.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs bg-blue-50 p-2 rounded">
                          <span>{file.name}</span>
                          <button
                            onClick={() => setNewMaoReports(newMaoReports.filter((_, i) => i !== idx))}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Upload Property Photos
                  </label>
                  <input
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png,.gif,.webp"
                    onChange={(e) => {
                      if (e.target.files) {
                        setNewPropertyPhotos([...newPropertyPhotos, ...Array.from(e.target.files)]);
                      }
                    }}
                    className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                  />
                  {newPropertyPhotos.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {newPropertyPhotos.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs bg-purple-50 p-2 rounded">
                          <span>{file.name}</span>
                          <button
                            onClick={() => setNewPropertyPhotos(newPropertyPhotos.filter((_, i) => i !== idx))}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Upload Additional Documents
                  </label>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      if (e.target.files) {
                        setNewContactLibrary([...newContactLibrary, ...Array.from(e.target.files)]);
                      }
                    }}
                    className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100"
                  />
                  {newContactLibrary.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {newContactLibrary.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs bg-amber-50 p-2 rounded">
                          <span>{file.name}</span>
                          <button
                            onClick={() => setNewContactLibrary(newContactLibrary.filter((_, i) => i !== idx))}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Remove Existing Files */}
              {(displayData.maoReportUrls?.length > 0 || displayData.propertyPhotoUrls?.length > 0 || displayData.additionalDocUrls?.length > 0) && (
                <div className="mt-4 pt-4 border-t border-blue-300">
                  <h4 className="font-semibold text-gray-800 mb-3 text-sm">Remove Existing Files</h4>

                  {displayData.maoReportUrls?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-600 mb-2">MAO Reports:</p>
                      <div className="space-y-1">
                        {displayData.maoReportUrls.filter((url: string) => !filesToRemove.includes(url)).map((url: string, idx: number) => (
                          <div key={idx} className="flex items-center justify-between text-xs bg-gray-50 p-2 rounded">
                            <span className="truncate flex-1">{url.split('/').pop()}</span>
                            <button
                              onClick={() => setFilesToRemove([...filesToRemove, url])}
                              className="text-red-600 hover:text-red-800 ml-2 flex items-center space-x-1"
                            >
                              <Trash2 className="w-3 h-3" />
                              <span>Remove</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {displayData.propertyPhotoUrls?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-600 mb-2">Property Photos:</p>
                      <div className="space-y-1">
                        {displayData.propertyPhotoUrls.filter((url: string) => !filesToRemove.includes(url)).map((url: string, idx: number) => (
                          <div key={idx} className="flex items-center justify-between text-xs bg-gray-50 p-2 rounded">
                            <span className="truncate flex-1">{url.split('/').pop()}</span>
                            <button
                              onClick={() => setFilesToRemove([...filesToRemove, url])}
                              className="text-red-600 hover:text-red-800 ml-2 flex items-center space-x-1"
                            >
                              <Trash2 className="w-3 h-3" />
                              <span>Remove</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {displayData.additionalDocUrls?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-600 mb-2">Additional Documents:</p>
                      <div className="space-y-1">
                        {displayData.additionalDocUrls.filter((url: string) => !filesToRemove.includes(url)).map((url: string, idx: number) => (
                          <div key={idx} className="flex items-center justify-between text-xs bg-gray-50 p-2 rounded">
                            <span className="truncate flex-1">{url.split('/').pop()}</span>
                            <button
                              onClick={() => setFilesToRemove([...filesToRemove, url])}
                              className="text-red-600 hover:text-red-800 ml-2 flex items-center space-x-1"
                            >
                              <Trash2 className="w-3 h-3" />
                              <span>Remove</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {filesToRemove.length > 0 && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs">
                      <p className="font-semibold text-red-800">
                        {filesToRemove.length} file(s) marked for removal
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Inline status bar — Admin/Deal-Engineer edit status + assigned-to in place; Partners read-only */}
          <div className="mb-6" data-testid="status-bar">
            {!isEditingStatusBar ? (
              <div className="flex items-center flex-wrap gap-3">
                <button
                  type="button"
                  data-testid="status-badge-button"
                  onClick={enterStatusBarEdit}
                  disabled={!isAdmin}
                  title={isAdmin ? 'Click to edit status' : undefined}
                  className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(status)} ${isAdmin ? 'cursor-pointer hover:ring-2 hover:ring-tdb-accent' : 'cursor-default'}`}
                >
                  {STATUS_OPTIONS.find(s => s.value === status)?.label || status}
                </button>
                <button
                  type="button"
                  data-testid="assignee-display-button"
                  onClick={enterStatusBarEdit}
                  disabled={!isAdmin}
                  title={isAdmin ? 'Click to edit assignment' : undefined}
                  className={`text-sm text-gray-600 ${isAdmin ? 'cursor-pointer hover:text-tdb-primary' : 'cursor-default'}`}
                >
                  Assigned to: <span className="font-semibold">{assignedTo || 'Unassigned'}</span>
                </button>
              </div>
            ) : (
              <div className="flex items-end flex-wrap gap-3 bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Status</label>
                  <select
                    data-testid="inline-status-select"
                    value={inlineStatus}
                    onChange={(e) => setInlineStatus(e.target.value)}
                    className="px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-tdb-accent"
                  >
                    {STATUS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="min-w-[200px]" data-testid="inline-assignee-selector">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Assigned To {!canAssign && <span className="text-xs text-gray-500">(Read-only)</span>}
                  </label>
                  {canAssign ? (
                    <AssigneeSelector assignedTo={inlineAssignedTo} onChange={setInlineAssignedTo} />
                  ) : (
                    <input
                      type="text"
                      value={inlineAssignedTo || 'Unassigned'}
                      readOnly
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-600 text-sm cursor-not-allowed"
                    />
                  )}
                </div>
                <div className="flex items-center gap-2 pb-0.5">
                  {hasStatusBarChanges && (
                    <button
                      type="button"
                      data-testid="status-bar-save-button"
                      onClick={handleStatusBarSave}
                      disabled={savingStatusBar}
                      className="px-4 py-2 bg-tdb-primary text-white rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                    >
                      {savingStatusBar ? 'Saving…' : 'Save'}
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid="status-bar-cancel-button"
                    onClick={() => setIsEditingStatusBar(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
                {statusBarError && (
                  <p className="w-full text-sm text-red-700" data-testid="status-bar-error">{statusBarError}</p>
                )}
              </div>
            )}
          </div>

          {/* Section 1: Partner Information */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-tdb-primary mb-4 flex items-center space-x-2 border-b-2 border-gray-200 pb-2">
              <User className="w-5 h-5" />
              <span>Partner Information</span>
            </h3>
            {editMode ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">First Name</label>
                  <input type="text" value={formData.partnerFirstName} onChange={(e) => updateFormField('partnerFirstName', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Last Name</label>
                  <input type="text" value={formData.partnerLastName} onChange={(e) => updateFormField('partnerLastName', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                  <input type="email" value={formData.partnerEmail} onChange={(e) => updateFormField('partnerEmail', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Phone</label>
                  <input type="tel" value={formData.partnerPhone} onChange={(e) => updateFormField('partnerPhone', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Tax Company</label>
                  <input type="text" value={formData.partnerTaxCompany} onChange={(e) => updateFormField('partnerTaxCompany', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Website</label>
                  <input type="url" value={formData.taxAssistanceWebsite} onChange={(e) => updateFormField('taxAssistanceWebsite', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div><span className="font-semibold">Name:</span> {displayData.partnerFirstName} {displayData.partnerLastName}</div>
                <div><span className="font-semibold">Email:</span> {displayData.partnerEmail}</div>
                <div><span className="font-semibold">Phone:</span> {displayData.partnerPhone}</div>
                <div><span className="font-semibold">Tax Company:</span> {displayData.partnerTaxCompany}</div>
                <div><span className="font-semibold">Website:</span> {displayData.taxAssistanceWebsite}</div>
              </div>
            )}
          </div>

          {/* Section 2: Owner/Contact Info */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-tdb-primary mb-4 flex items-center space-x-2 border-b-2 border-gray-200 pb-2">
              <Users className="w-5 h-5" />
              <span>Owner & Contact Information</span>
            </h3>
            {editMode ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Owner First Name</label>
                  <input type="text" value={formData.ownerFirstName} onChange={(e) => updateFormField('ownerFirstName', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Owner Last Name</label>
                  <input type="text" value={formData.ownerLastName} onChange={(e) => updateFormField('ownerLastName', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Owner Phone</label>
                  <input type="tel" value={formData.ownerPhone} onChange={(e) => updateFormField('ownerPhone', formatPhoneNumber(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Owner Email</label>
                  <input type="email" value={formData.ownerEmail} onChange={(e) => updateFormField('ownerEmail', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Mailing Street</label>
                  <input type="text" value={formData.mailingStreet} onChange={(e) => updateFormField('mailingStreet', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Mailing City</label>
                  <input type="text" value={formData.mailingCity} onChange={(e) => updateFormField('mailingCity', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Mailing State</label>
                  <input type="text" value={formData.mailingState} onChange={(e) => updateFormField('mailingState', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Mailing Zip</label>
                  <input type="text" value={formData.mailingZip} onChange={(e) => updateFormField('mailingZip', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Best Contact Method</label>
                  <select value={formData.bestContactMethod} onChange={(e) => updateFormField('bestContactMethod', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">Select...</option>
                    <option value="phone">Phone Call</option>
                    <option value="text">Text Message</option>
                    <option value="email">Email</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Best Contact Time</label>
                  <select value={formData.bestContactTime} onChange={(e) => updateFormField('bestContactTime', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">Select...</option>
                    <option value="morning">8:00 AM to 11:00 AM EST</option>
                    <option value="afternoon">12:00 PM to 5:00 PM EST</option>
                    <option value="evening">6:00 PM to 8:00 PM EST</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Deal Type</label>
                  <select value={formData.dealType} onChange={(e) => updateFormField('dealType', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">Select...</option>
                    <option value="cash-offer">Standing Cash Offer - Wholesale</option>
                    <option value="lease-option">Lease With Option to Purchase</option>
                    <option value="rent-back">Rent-Back With Option to Purchase</option>
                  </select>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div><span className="font-semibold">Owner Name:</span> {displayData.ownerFirstName} {displayData.ownerLastName}</div>
                <div><span className="font-semibold">Owner Phone:</span> {displayData.ownerPhone}</div>
                <div><span className="font-semibold">Owner Email:</span> {displayData.ownerEmail}</div>
                <div><span className="font-semibold">Mailing Address:</span> {displayData.mailingStreet}, {displayData.mailingCity}, {displayData.mailingState} {displayData.mailingZip}</div>
                <div><span className="font-semibold">Best Contact Method:</span> {displayData.bestContactMethod}</div>
                <div><span className="font-semibold">Best Contact Time:</span> {displayData.bestContactTime}</div>
                <div className="md:col-span-2"><span className="font-semibold">Deal Type:</span> {displayData.dealType}</div>
              </div>
            )}
          </div>

          {/* Section: Seller Financial Situation (moved up per Reham's request - income/expenses near owner info) */}
          {displayData.employmentStatus && (
            <div className="mb-6">
              <h3 className="text-lg font-bold text-tdb-primary mb-4 flex items-center space-x-2 border-b-2 border-gray-200 pb-2">
                <FileText className="w-5 h-5" />
                <span>Seller Financial Situation</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div><span className="font-semibold">Employment:</span> {displayData.employmentStatus}</div>
                <div><span className="font-semibold">Monthly Income:</span> {displayData.monthlyIncome}</div>
                {displayData.incomeBreakdown && (
                  <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg">
                    <p className="font-semibold mb-2">Income Breakdown:</p>
                    <p className="text-gray-700">{displayData.incomeBreakdown}</p>
                  </div>
                )}
                {displayData.sellerRealistic && (
                  <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg">
                    <p className="font-semibold mb-2">Seller Realistic About Situation:</p>
                    <p className="text-gray-700">{displayData.sellerRealistic}</p>
                  </div>
                )}
                {displayData.optionsDiscussed && (
                  <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg">
                    <p className="font-semibold mb-2">Options Discussed:</p>
                    <p className="text-gray-700">{displayData.optionsDiscussed}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Section 3: Property Details */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-tdb-primary mb-4 flex items-center space-x-2 border-b-2 border-gray-200 pb-2">
              <Building2 className="w-5 h-5" />
              <span>Property Details</span>
            </h3>
            {editMode ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Property Street</label>
                  <input type="text" value={formData.propertyStreet} onChange={(e) => updateFormField('propertyStreet', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">City</label>
                  <input type="text" value={formData.propertyCity} onChange={(e) => updateFormField('propertyCity', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">State</label>
                  <input type="text" value={formData.propertyState} onChange={(e) => updateFormField('propertyState', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Zip Code</label>
                  <input type="text" value={formData.propertyZip} onChange={(e) => updateFormField('propertyZip', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">County</label>
                  <input type="text" value={formData.propertyCounty} onChange={(e) => updateFormField('propertyCounty', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Tax Sale Date</label>
                  <input type="date" value={formData.taxSaleDate} onChange={(e) => updateFormField('taxSaleDate', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Tax Sale Amount</label>
                  <input type="text" value={formData.taxSaleAmount} onChange={(e) => updateFormField('taxSaleAmount', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Bedrooms</label>
                  <select value={formData.bedrooms} onChange={(e) => updateFormField('bedrooms', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">Select...</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5+">5+</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Bathrooms</label>
                  <select value={formData.bathrooms} onChange={(e) => updateFormField('bathrooms', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">Select...</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5+">5+</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Square Feet</label>
                  <input type="number" value={formData.sqft} onChange={(e) => updateFormField('sqft', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Ownership Length</label>
                  <input type="text" value={formData.ownershipLength} onChange={(e) => updateFormField('ownershipLength', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Insurance</label>
                  <select value={formData.hasInsurance} onChange={(e) => updateFormField('hasInsurance', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">Select...</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Repair Cost</label>
                  <input type="text" value={formData.repairCost} onChange={(e) => updateFormField('repairCost', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Property Condition</label>
                  <textarea value={formData.propertyCondition} onChange={(e) => updateFormField('propertyCondition', e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">County Tax Rules</label>
                  <textarea value={formData.countyTaxRules} onChange={(e) => updateFormField('countyTaxRules', e.target.value)} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Public Tax Record Link</label>
                  <input type="url" value={formData.publicTaxRecordLink} onChange={(e) => updateFormField('publicTaxRecordLink', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Has Mortgage?</label>
                  <select value={formData.hasMortgage} onChange={(e) => updateFormField('hasMortgage', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">Select...</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                {formData.hasMortgage === 'yes' && (
                  <>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Mortgage Bank</label>
                      <input type="text" value={formData.mortgageBank} onChange={(e) => updateFormField('mortgageBank', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Mortgage Balance</label>
                      <input type="text" value={formData.mortgageBalance} onChange={(e) => updateFormField('mortgageBalance', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Monthly Payment</label>
                      <input type="text" value={formData.mortgagePayment} onChange={(e) => updateFormField('mortgagePayment', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="md:col-span-2"><span className="font-semibold">Address:</span> {displayData.propertyStreet}, {displayData.propertyCity}, {displayData.propertyState} {displayData.propertyZip}</div>
                <div><span className="font-semibold">County:</span> {displayData.propertyCounty}</div>
                <div><span className="font-semibold">Tax Sale Date:</span> {displayData.taxSaleDate}</div>
                <div><span className="font-semibold">Tax Sale Amount:</span> {displayData.taxSaleAmount}</div>
                <div><span className="font-semibold">Bedrooms:</span> {displayData.bedrooms}</div>
                <div><span className="font-semibold">Bathrooms:</span> {displayData.bathrooms}</div>
                <div><span className="font-semibold">Sq Ft:</span> {displayData.sqft}</div>
                <div><span className="font-semibold">Ownership Length:</span> {displayData.ownershipLength}</div>
                <div><span className="font-semibold">Insurance:</span> {displayData.hasInsurance}</div>
                <div><span className="font-semibold">Repair Cost:</span> {displayData.repairCost}</div>
              </div>
            )}
            {!editMode && (
              <div className="mt-4 space-y-4">
              {displayData.hasMortgage === 'yes' && (
                <>
                  <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg">
                    <p className="font-semibold mb-2">Mortgage Information:</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>Bank: {displayData.mortgageBank}</div>
                      <div>Balance: {displayData.mortgageBalance}</div>
                      <div>Monthly Payment: {displayData.mortgagePayment}</div>
                    </div>
                  </div>
                </>
              )}
              <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold mb-2 text-sm">Property Condition:</p>
                <p className="text-gray-700 text-sm">{displayData.propertyCondition}</p>
              </div>
              {displayData.countyTaxRules && (
                <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg">
                  <p className="font-semibold mb-2 text-sm">County Tax Rules:</p>
                  <p className="text-gray-700 text-sm">{displayData.countyTaxRules}</p>
                </div>
              )}
              {displayData.publicTaxRecordLink && (
                <div className="md:col-span-2 text-sm">
                  <span className="font-semibold">Tax Record:</span> <a href={displayData.publicTaxRecordLink} target="_blank" rel="noopener noreferrer" className="text-tdb-accent hover:underline">{displayData.publicTaxRecordLink}</a>
                </div>
              )}
              </div>
            )}
          </div>

          {/* Section 4: Financial Analysis */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-tdb-primary mb-4 flex items-center space-x-2 border-b-2 border-gray-200 pb-2">
              <FileText className="w-5 h-5" />
              <span>Financial Analysis</span>
            </h3>
            {editMode ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">ARV</label>
                  <input type="text" value={formData.arv} onChange={(e) => updateFormField('arv', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Suggested Offer</label>
                  <input type="text" value={formData.suggestedOffer} onChange={(e) => updateFormField('suggestedOffer', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 text-sm mb-4">
                <div><span className="font-semibold">ARV:</span> {displayData.arv || 'N/A'}</div>
                <div><span className="font-semibold">Suggested Offer:</span> {displayData.suggestedOffer}</div>
              </div>
            )}

            {/* Closing Details — admins always see the inputs; partners see a card only once filled */}
            {isAdmin && onSave ? (
              <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="closing-details-admin">
                <p className="font-semibold text-gray-800 mb-1">Closing Details</p>
                <p className="text-xs text-gray-500 mb-3">Shown to the partner on their deal once filled in. Leave both blank to hide.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Property listing link</label>
                    <input
                      type="url"
                      data-testid="closing-listing-link-input"
                      value={closingLink}
                      onChange={(e) => setClosingLink(e.target.value)}
                      placeholder="https://…"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-tdb-accent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Property sale amount</label>
                    <input
                      type="text"
                      data-testid="closing-sale-amount-input"
                      value={closingAmount}
                      onChange={(e) => setClosingAmount(e.target.value)}
                      placeholder="$185,000"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-tdb-accent"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <button
                    type="button"
                    data-testid="closing-details-save-button"
                    onClick={handleSaveClosingDetails}
                    disabled={savingClosing}
                    className="px-4 py-2 rounded-lg bg-tdb-accent text-white text-sm font-semibold hover:bg-opacity-90 disabled:opacity-50"
                  >
                    {savingClosing ? 'Saving…' : 'Save Closing Details'}
                  </button>
                  {closingSaved && !closingError && (
                    <span className="text-xs font-semibold text-green-700" data-testid="closing-details-saved">Saved</span>
                  )}
                  {closingError && (
                    <span className="text-xs text-red-700" data-testid="closing-details-error">{closingError}</span>
                  )}
                </div>
              </div>
            ) : (displayData.propertyListingLink || displayData.propertySaleAmount) ? (
              <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4" data-testid="closing-details-card">
                <p className="font-semibold text-gray-800 mb-2">Closing Details</p>
                {displayData.propertyListingLink && (
                  <p className="text-sm mb-1">
                    <span className="font-semibold">Property listing:</span>{' '}
                    <a
                      href={displayData.propertyListingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-tdb-accent underline break-all"
                      data-testid="closing-listing-link"
                    >
                      {displayData.propertyListingLink}
                    </a>
                  </p>
                )}
                {displayData.propertySaleAmount && (
                  <p className="text-sm">
                    <span className="font-semibold">Sale amount:</span>{' '}
                    <span data-testid="closing-sale-amount">{displayData.propertySaleAmount}</span>
                  </p>
                )}
              </div>
            ) : null}
            <div className="grid grid-cols-1 gap-4 text-sm">

              {/* MAO Reports - View/Download */}
              {displayData.maoReportUrls && displayData.maoReportUrls.length > 0 && (
                <div className="md:col-span-2">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="font-semibold text-gray-800 mb-3 flex items-center">
                      <FileText className="w-4 h-4 mr-2" />
                      MAO Reports ({displayData.maoReportUrls.length})
                    </p>
                    <div className="space-y-2">
                      {displayData.maoReportUrls.map((url: string, idx: number) => {
                        const fileName = url.split('/').pop()?.split('?')[0] || `mao-report-${idx + 1}`;
                        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
                        const presignedUrl = displayData.maoReportUrlsPresigned?.[idx] || url;
                        return (
                          <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200">
                            <span className="text-sm text-gray-700 truncate flex-1">{fileName}</span>
                            <div className="flex items-center space-x-2 ml-4">
                              {isImage && (
                                <button
                                  onClick={() => window.open(presignedUrl, '_blank')}
                                  className="text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                                  title="View image"
                                >
                                  <Eye className="w-4 h-4" />
                                  <span className="text-xs">View</span>
                                </button>
                              )}
                              <a
                                href={presignedUrl}
                                download
                                className="text-green-600 hover:text-green-800 flex items-center space-x-1"
                                title="Download file"
                              >
                                <Upload className="w-4 h-4 rotate-180" />
                                <span className="text-xs">Download</span>
                              </a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Property Photos - View/Download */}
              {displayData.propertyPhotoUrls && displayData.propertyPhotoUrls.length > 0 && (
                <div className="md:col-span-2">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="font-semibold text-gray-800 mb-3 flex items-center">
                      <Image className="w-4 h-4 mr-2" />
                      Property Photos ({displayData.propertyPhotoUrls.length})
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {displayData.propertyPhotoUrls.map((url: string, idx: number) => {
                        const fileName = url.split('/').pop()?.split('?')[0] || `photo-${idx + 1}`;
                        const presignedUrl = displayData.propertyPhotoUrlsPresigned?.[idx] || url;
                        return (
                          <div key={idx} className="relative group">
                            <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden">
                              <img
                                src={presignedUrl}
                                alt={fileName}
                                className="w-full h-full object-cover cursor-pointer hover:opacity-75 transition-opacity"
                                onClick={() => window.open(presignedUrl, '_blank')}
                              />
                            </div>
                            <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <a
                                href={presignedUrl}
                                download
                                className="bg-white rounded-full p-2 shadow-lg text-green-600 hover:text-green-800"
                                title="Download"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Upload className="w-4 h-4 rotate-180" />
                              </a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Documents - View/Download */}
              {displayData.additionalDocUrls && displayData.additionalDocUrls.length > 0 && (
                <div className="md:col-span-2">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="font-semibold text-gray-800 mb-3 flex items-center">
                      <FileText className="w-4 h-4 mr-2" />
                      Additional Documents ({displayData.additionalDocUrls.length})
                    </p>
                    <div className="space-y-2">
                      {displayData.additionalDocUrls.map((url: string, idx: number) => {
                        const fileName = url.split('/').pop()?.split('?')[0] || `document-${idx + 1}`;
                        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
                        const isAudio = /\.(mp3|wav|m4a|ogg|aac|flac)$/i.test(fileName);
                        const presignedUrl = displayData.additionalDocUrlsPresigned?.[idx] || url;
                        return (
                          <div key={idx} className="bg-white p-3 rounded border border-gray-200">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm text-gray-700 truncate flex-1">{fileName}</span>
                              <div className="flex items-center space-x-2 ml-4">
                                {isImage && (
                                  <button
                                    onClick={() => window.open(presignedUrl, '_blank')}
                                    className="text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                                    title="View image"
                                  >
                                    <Eye className="w-4 h-4" />
                                    <span className="text-xs">View</span>
                                  </button>
                                )}
                                <a
                                  href={presignedUrl}
                                  download
                                  className="text-green-600 hover:text-green-800 flex items-center space-x-1"
                                  title="Download file"
                                >
                                  <Upload className="w-4 h-4 rotate-180" />
                                  <span className="text-xs">Download</span>
                                </a>
                              </div>
                            </div>
                            {isAudio && (
                              <audio controls className="w-full mt-2" preload="none">
                                <source src={presignedUrl} />
                              </audio>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Comments Section - Tabbed view */}
          <div ref={commentsRef} className="bg-gray-50 rounded-xl p-6 mt-6">
            {/* Tab Navigation (only show for admins) */}
            {isAdmin && (
              <div className="flex space-x-1 mb-4 border-b border-gray-200">
                <button
                  onClick={() => setCommentTab('user')}
                  className={`px-4 py-2 font-semibold text-sm transition-colors ${
                    commentTab === 'user'
                      ? 'text-tdb-accent border-b-2 border-tdb-accent'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  User Comments
                </button>
                <button
                  onClick={() => setCommentTab('admin')}
                  className={`px-4 py-2 font-semibold text-sm transition-colors ${
                    commentTab === 'admin'
                      ? 'text-purple-600 border-b-2 border-purple-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Admin-Only Discussion
                </button>
              </div>
            )}

            {/* Render appropriate comments based on tab */}
            <CommentsSection
              submissionId={displayData.id}
              isAdminView={isAdmin}
              adminOnly={isAdmin && commentTab === 'admin'}
            />
          </div>

          {/* Status History timeline (admin-only) — from the tdb-jv-history ledger */}
          {isAdmin && history && history.length > 0 && (
            <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-5">
              <h3 className="text-lg font-bold text-tdb-primary mb-4 flex items-center space-x-2 border-b-2 border-gray-200 pb-2">
                <Clock className="w-5 h-5" />
                <span>Status History</span>
              </h3>
              <ol className="space-y-3">
                {history.map((ev: any, i: number) => (
                  <li key={i} className="flex items-start gap-3 text-sm" data-testid="history-event">
                    <span className="mt-1.5 w-2 h-2 rounded-full bg-tdb-accent flex-shrink-0" />
                    <div>
                      <div className="font-medium text-gray-900">
                        {ev.fromStatus ? `${ev.fromStatus} → ${ev.toStatus}` : `Created (${ev.toStatus || 'new'})`}
                      </div>
                      <div className="text-gray-500 text-xs">
                        {ev.changedBy || 'system'}
                        {ev.changedAt ? ` · ${new Date(ev.changedAt).toLocaleString()}` : ''}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Request & Escalation contact panel — at the bottom, by the comments */}
          <div className="mt-6 mb-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3">
              {/* Button 1 — Request Deal Update */}
              <button
                type="button"
                onClick={() => { setShowRequestForm(true); setShowEscalationForm(false); setRequestMsg(''); }}
                className="group flex items-start gap-3 rounded-lg border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 p-4 text-left transition-all hover:border-orange-400 hover:shadow-md"
              >
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-orange-500 text-sm font-bold text-white shadow">1</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Send className="w-4 h-4 text-orange-600" />
                    <span className="text-sm font-semibold text-orange-800">Request Deal Update</span>
                  </div>
                  <p className="mt-0.5 text-xs font-medium text-orange-600">Pipeline Manager Notification</p>
                  <p className="mt-1.5 text-xs leading-relaxed text-gray-500">
                    Use this first. Whether you need a status update, a document, a call recording, or just have a question — this is your starting point.
                  </p>
                </div>
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 px-2">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-[11px] font-bold tracking-widest text-gray-400">THEN</span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>

              {/* Button 2 — Escalate Concern */}
              <button
                type="button"
                onClick={() => { setShowEscalationForm(true); setShowRequestForm(false); setEscalationMsg(''); }}
                className="group flex items-start gap-3 rounded-lg border-2 border-red-200 bg-gradient-to-r from-red-50 to-rose-50 p-4 text-left transition-all hover:border-red-400 hover:shadow-md"
              >
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-500 text-sm font-bold text-white shadow">2</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-red-800">Escalate Concern</span>
                  </div>
                  <p className="mt-0.5 text-xs font-medium text-red-600">COO Notification</p>
                  <p className="mt-1.5 text-xs leading-relaxed text-gray-500">
                    Use this only if more than 48 hours have passed since your last request with no response.
                  </p>
                  <p className="mt-2 rounded bg-gray-100 p-2 text-[11px] leading-relaxed text-gray-400">
                    Escalations to chief executives are not reviewed independently — they are passed directly back to the team and only cause delays. Please complete Steps 1 and 2 first.
                  </p>
                </div>
              </button>
            </div>

            {/* Request form */}
            {showRequestForm && (
              <div className="mt-4 rounded-lg border border-orange-200 bg-orange-50/50 p-4">
                <h4 className="mb-3 text-sm font-semibold text-orange-800">New Request</h4>
                <label className="mb-1 block text-xs font-medium text-gray-700">Type of Request</label>
                <select
                  value={requestType}
                  onChange={(e) => { setRequestType(e.target.value); setRequestDetails(''); }}
                  className="mb-3 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400"
                >
                  <option value="">Select a request type...</option>
                  <option value="Document Request">Document Request</option>
                  <option value="Call Recording Request">Call Recording Request</option>
                  <option value="Status Update Request">Status Update Request</option>
                  <option value="Something Else">Something Else</option>
                </select>
                {requestType === 'Something Else' && (
                  <>
                    <label className="mb-1 block text-xs font-medium text-gray-700">Please describe your request</label>
                    <textarea
                      value={requestDetails}
                      onChange={(e) => setRequestDetails(e.target.value)}
                      rows={4}
                      className="mb-3 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400"
                      placeholder="Describe what you need..."
                    />
                  </>
                )}
                {requestMsg && (
                  <p className={`mb-3 text-sm font-medium ${requestMsg.includes('✅') ? 'text-green-600' : 'text-red-600'}`}>{requestMsg}</p>
                )}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleSubmitRequest}
                    disabled={contactSubmitting || !requestType}
                    className="rounded-md bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {contactSubmitting ? 'Sending...' : 'Send Request'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowRequestForm(false); setRequestType(''); setRequestDetails(''); setRequestMsg(''); }}
                    className="rounded-md px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Escalation form */}
            {showEscalationForm && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50/50 p-4">
                <h4 className="mb-3 text-sm font-semibold text-red-800">New Escalation</h4>
                <label className="mb-1 block text-xs font-medium text-gray-700">Subject</label>
                <input
                  type="text"
                  value={escalationSubject}
                  onChange={(e) => setEscalationSubject(e.target.value)}
                  className="mb-3 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-red-400 focus:outline-none focus:ring-1 focus:ring-red-400"
                  placeholder="Brief subject line..."
                />
                <label className="mb-1 block text-xs font-medium text-gray-700">Details</label>
                <textarea
                  value={escalationDetails}
                  onChange={(e) => setEscalationDetails(e.target.value)}
                  rows={4}
                  className="mb-3 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-red-400 focus:outline-none focus:ring-1 focus:ring-red-400"
                  placeholder="Describe the issue and what you need..."
                />
                {escalationMsg && (
                  <p className={`mb-3 text-sm font-medium ${escalationMsg.includes('✅') ? 'text-green-600' : 'text-red-600'}`}>{escalationMsg}</p>
                )}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleSubmitEscalation}
                    disabled={contactSubmitting || !escalationSubject.trim() || !escalationDetails.trim()}
                    className="rounded-md bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {contactSubmitting ? 'Sending...' : 'Send Escalation'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowEscalationForm(false); setEscalationSubject(''); setEscalationDetails(''); setEscalationMsg(''); }}
                    className="rounded-md px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-8 py-4 flex items-center justify-between border-t-2 border-gray-200 flex-shrink-0">
          <div className="text-xs text-gray-500">
            Submitted: {new Date(displayData.createdAt).toLocaleString()}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 font-semibold"
            >
              Close
            </button>
            {editMode && onSave && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-tdb-accent text-white rounded-lg hover:bg-[#0ea5ef] font-semibold disabled:opacity-50 flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Property Analysis popup — admin only, shown when BarChart2 button clicked */}
      {showCompsPopup && isAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col">
            <div className="bg-gradient-to-r from-tdb-primary to-tdb-secondary text-white px-6 py-4 flex items-center justify-between flex-shrink-0 rounded-t-2xl">
              <div className="flex items-center space-x-2">
                <BarChart2 className="w-5 h-5" />
                <span className="font-bold text-lg">Property Analysis</span>
                <span className="text-sm text-gray-200 ml-2">{displayData.propertyStreet}</span>
              </div>
              <button
                onClick={() => setShowCompsPopup(false)}
                className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <PropertyCompsSection
                submissionId={displayData.id}
                claimedArv={displayData.arv}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
